/**
 * TimeMotionOverlay - Time navigation UI for chart time shifting
 *
 * Renders inside SVG foreignObject in chart-center for automatic positioning during resize.
 *
 * Shows:
 * - Loading spinner while buffer is loading
 * - Navigation buttons < [time] > when ready
 * - Click: ±1 step, Shift+Click: ±10 steps
 * - Hold: auto-repeat
 *
 * Unlike PlanetDragOverlay (which follows one planet), this shows the chart time
 * shifting forward/backward in time with all planets updating.
 */

import { devLog } from '../../../utils/devLog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { globalEventStream, createEvent } from './universal_architecture/core/EventStream';
import {
  NavButton,
  BufferIndicator,
  ActionButtons,
  NavigationHint,
  formatTime,
  formatDate,
  formatOffsetFromSeconds,
  SHIFT_MULTIPLIER,
} from './MotionOverlayComponents';
import type { BaseEvent, EventSubscription } from './universal_architecture/types/EventTypes';
import type { TimeStepUnit } from '../../../types/MotionTypes';
import { setActiveTimeMotion, clearActiveMotion } from './shared/motionState';
import './PlanetDragOverlay.css'; // Reuse the same styles

const DEBUG = false;

// Hold-to-repeat timing
const HOLD_REPEAT_DELAY_MS = 500;
const HOLD_REPEAT_INTERVAL_MS = 200;

// Buffer size for time motion (601 points: 300 on each side + center)
const TIME_BUFFER_SIZE = 601;
const TIME_CENTER_INDEX = 300;

// =============================================================================
// Types
// =============================================================================

interface TimeMotionOverlayProps {
  chartId: string;
}

type OverlayState =
  | { type: 'hidden' }
  | { type: 'loading'; stepUnit: TimeStepUnit; stepValue: number; progress: number; originalChartTime?: Date }
  | {
      type: 'navigating';
      stepUnit: TimeStepUnit;
      stepValue: number;
      currentTime: Date;
      originalChartTime: Date; // Original chart time (never changes during session)
      currentIndex: number;
      centerIndex: number;
      canGoBack: boolean;
      canGoForward: boolean;
      timezoneCorrection: number;
      isPartial?: boolean;
      loadingProgress?: number;
    };

// =============================================================================
// Component
// =============================================================================

export const TimeMotionOverlay: React.FC<TimeMotionOverlayProps> = ({ chartId }) => {
  const [state, setState] = useState<OverlayState>({ type: 'hidden' });
  const [waitingDirection, setWaitingDirection] = useState<'back' | 'forward' | null>(null);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isShiftPressedRef = useRef(false);

  // ===========================================================================
  // Portal target - find foreignObject container in SVG
  // ===========================================================================

  useEffect(() => {
    const findPortalTarget = () => {
      const targetId = `chart-center-overlay-${chartId}`;
      const target = document.getElementById(targetId);
      if (target && target !== portalTarget) {
        setPortalTarget(target);
        if (DEBUG) devLog(`[TimeMotion:Overlay] Found portal target: #${targetId}`);
      }
    };

    findPortalTarget();

    const observer = new MutationObserver(() => {
      if (!portalTarget) {
        findPortalTarget();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [chartId, portalTarget]);

  // ===========================================================================
  // Keyboard tracking for Shift
  // ===========================================================================

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') isShiftPressedRef.current = true;
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') isShiftPressedRef.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // ===========================================================================
  // Navigation
  // ===========================================================================

  const navigate = useCallback((direction: 'back' | 'forward') => {
    if (state.type !== 'navigating') return;

    const steps = isShiftPressedRef.current ? SHIFT_MULTIPLIER : 1;
    const indexDelta = direction === 'forward' ? steps : -steps;

    if (DEBUG) devLog(`[TimeMotion:Overlay] Navigate ${direction} by ${indexDelta} index(es)`);

    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.navigate',
      'TimeMotionOverlay',
      {
        metadata: {
          chartId,
          indexDelta,
        },
      }
    ));
  }, [chartId, state]);

  const handleConfirm = useCallback(() => {
    if (state.type !== 'navigating') return;

    // Preserve full precision (backend handles microseconds)
    const selectedTimeISO = state.currentTime.toISOString();

    if (DEBUG) devLog(`[TimeMotion:Overlay] Confirm at ${selectedTimeISO}`);

    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.confirm',
      'TimeMotionOverlay',
      {
        metadata: {
          chartId,
          selectedTime: selectedTimeISO,
        },
      }
    ));

    setWaitingDirection(null);
    setState({ type: 'hidden' });
    clearActiveMotion(chartId);

    // Emit overlay closed event
    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.overlay.closed',
      'TimeMotionOverlay',
      { metadata: { chartId } }
    ));
  }, [chartId, state]);

  const handleCancel = useCallback(() => {
    if (DEBUG) devLog(`[TimeMotion:Overlay] Cancel`);

    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.cancel',
      'TimeMotionOverlay',
      { metadata: { chartId } }
    ));

    setWaitingDirection(null);
    setState({ type: 'hidden' });
    clearActiveMotion(chartId);

    // Emit overlay closed event
    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.overlay.closed',
      'TimeMotionOverlay',
      { metadata: { chartId } }
    ));
  }, [chartId]);

  // ===========================================================================
  // Hold-to-repeat
  // ===========================================================================

  const startHold = useCallback((direction: 'back' | 'forward') => {
    stopHold();
    navigate(direction);

    holdTimerRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        navigate(direction);
      }, HOLD_REPEAT_INTERVAL_MS);
    }, HOLD_REPEAT_DELAY_MS);
  }, [navigate]);

  const stopHold = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  }, []);

  const handleDisabledClick = useCallback((direction: 'back' | 'forward') => {
    if (state.type !== 'navigating') return;
    if (!state.isPartial) return;

    if (DEBUG) devLog(`[TimeMotion:Overlay] Waiting for ${direction} data...`);
    setWaitingDirection(direction);
  }, [state]);

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  // Handle start event (emitted by useTimeMotionState after receiving time.motion.open)
  const handleStart = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    const stepUnit = event.metadata.stepUnit as TimeStepUnit;
    const stepValue = event.metadata.stepValue as number;
    const originalChartTimeStr = event.metadata.originalChartTime as string | undefined;
    const originalChartTime = originalChartTimeStr ? new Date(originalChartTimeStr) : undefined;

    if (DEBUG) devLog(`[TimeMotion:Overlay] Start: ${stepValue} ${stepUnit}`);

    // Cancel any active planet drag first
    globalEventStream.emit(createEvent<BaseEvent>(
      'planet.drag.cancel',
      'TimeMotionOverlay',
      { metadata: { chartId } }
    ));

    setState({ type: 'loading', stepUnit, stepValue, progress: 0, originalChartTime });

    // Set shared state for late-mounting components
    setActiveTimeMotion(chartId);

    // Emit overlay opened event
    globalEventStream.emit(createEvent<BaseEvent>(
      'time.motion.overlay.opened',
      'TimeMotionOverlay',
      { metadata: { chartId } }
    ));
  }, [chartId]);

  const handleProgress = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    const progress = event.metadata.progress as number;
    setState(prev => {
      if (prev.type === 'loading') {
        return { ...prev, progress };
      }
      if (prev.type === 'navigating' && prev.isPartial) {
        const isComplete = progress >= 100;
        return {
          ...prev,
          loadingProgress: progress,
          isPartial: !isComplete,
        };
      }
      return prev;
    });
  }, [chartId]);

  const handleReady = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    const stepUnit = event.metadata.stepUnit as TimeStepUnit;
    const stepValue = event.metadata.stepValue as number;
    const timestamp = event.metadata.timestamp as string;
    const currentIndex = event.metadata.currentIndex as number;
    const centerIndex = event.metadata.centerIndex as number;
    const canGoBack = event.metadata.canGoBack as boolean;
    const canGoForward = event.metadata.canGoForward as boolean;
    const isPartial = event.metadata.isPartial as boolean || false;
    const progress = (event.metadata.progress as number) || 100;
    const chartTimezoneOffset = (event.metadata.chartTimezoneOffset as number) || 0;
    const apiUtcOffset = (event.metadata.apiUtcOffset as number) || 0;
    const timezoneCorrection = chartTimezoneOffset - apiUtcOffset;

    if (DEBUG) devLog(`[TimeMotion:Overlay] Ready: ${stepValue}${stepUnit}, partial: ${isPartial}, progress: ${progress}%`);

    setWaitingDirection(prev => {
      if (prev === 'back' && canGoBack) return null;
      if (prev === 'forward' && canGoForward) return null;
      return prev;
    });

    const currentTime = new Date(timestamp);

    setState(prev => {
      // Preserve originalChartTime from loading state, or use currentTime as fallback
      const originalChartTime = (prev.type === 'loading' && prev.originalChartTime)
        ? prev.originalChartTime
        : (prev.type === 'navigating' ? prev.originalChartTime : currentTime);

      return {
        type: 'navigating',
        stepUnit,
        stepValue,
        currentTime,
        originalChartTime,
        currentIndex,
        centerIndex,
        canGoBack,
        canGoForward,
        timezoneCorrection,
        isPartial,
        loadingProgress: progress,
      };
    });
  }, [chartId]);

  const handleNavigationUpdate = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;

    const newIndex = event.metadata.newIndex as number;
    const timestamp = event.metadata.timestamp as string;
    const canGoBack = event.metadata.canGoBack as boolean;
    const canGoForward = event.metadata.canGoForward as boolean;
    const isPartial = event.metadata.isPartial as boolean | undefined;
    // apiUtcOffset available in event.metadata.apiUtcOffset if needed

    setWaitingDirection(prev => {
      if (prev === 'back' && canGoBack) return null;
      if (prev === 'forward' && canGoForward) return null;
      return prev;
    });

    setState(prev => {
      if (prev.type !== 'navigating') return prev;
      return {
        ...prev,
        currentIndex: newIndex,
        currentTime: new Date(timestamp),
        canGoBack,
        canGoForward,
        isPartial: isPartial !== undefined ? isPartial : prev.isPartial,
        // timezoneCorrection preserved from handleReady via ...prev
      };
    });
  }, [chartId]);

  const handleSettingsChanged = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;
    if (state.type !== 'navigating') return;

    const stepUnit = event.metadata.stepUnit as TimeStepUnit;
    const stepValue = event.metadata.stepValue as number;

    if (DEBUG) devLog(`[TimeMotion:Overlay] Settings changed: ${stepValue} ${stepUnit}`);

    // Preserve originalChartTime when changing settings
    setState({
      type: 'loading',
      stepUnit,
      stepValue,
      progress: 0,
      originalChartTime: state.originalChartTime,
    });

    // Note: useTimeMotionState listens to time.motion.settings.changed and handles loading
    // with current position as new center. No need to emit load request here.
  }, [chartId, state]);

  const handleClose = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;
    stopHold();
    setWaitingDirection(null);
    setState({ type: 'hidden' });
    clearActiveMotion(chartId);
  }, [chartId, stopHold]);

  // Close overlay when another chart is activated
  const handleChartActivated = useCallback((event: BaseEvent) => {
    const activatedChartId = event.metadata?.chartId || event.chartId;
    if (activatedChartId && activatedChartId !== chartId && state.type !== 'hidden') {
      if (DEBUG) devLog(`[TimeMotion:Overlay] Closing - another chart activated: ${activatedChartId}`);
      stopHold();
      setWaitingDirection(null);
      setState({ type: 'hidden' });
      clearActiveMotion(chartId);

      // Emit cancel to restore original planet positions
      globalEventStream.emit(createEvent<BaseEvent>(
        'time.motion.cancel',
        'TimeMotionOverlay',
        { metadata: { chartId } }
      ));

      globalEventStream.emit(createEvent<BaseEvent>(
        'time.motion.overlay.closed',
        'TimeMotionOverlay',
        { metadata: { chartId } }
      ));
    }
  }, [chartId, state.type, stopHold]);

  // Handle motion state query - respond if we're active
  const handleMotionStateQuery = useCallback((event: BaseEvent) => {
    if (event.metadata?.chartId !== chartId) return;
    if (state.type === 'hidden') return;

    if (DEBUG) devLog(`[TimeMotion:Overlay] Responding to motion.state.query`);
    globalEventStream.emit(createEvent<BaseEvent>(
      'motion.state.response',
      'TimeMotionOverlay',
      {
        metadata: {
          chartId,
          motionType: 'time-motion',
          planet: null,
        },
      }
    ));
  }, [chartId, state.type]);

  // ===========================================================================
  // Effect: Subscribe to events
  // ===========================================================================

  useEffect(() => {
    const subscriptions: EventSubscription[] = [
      globalEventStream.subscribe('time.motion.start', handleStart),
      globalEventStream.subscribe('time.motion.progress', handleProgress),
      globalEventStream.subscribe('time.motion.ready', handleReady),
      globalEventStream.subscribe('time.motion.navigation.update', handleNavigationUpdate),
      globalEventStream.subscribe('time.motion.close', handleClose),
      globalEventStream.subscribe('time.motion.settings.changed', handleSettingsChanged),
      globalEventStream.subscribe('chart:activated', handleChartActivated),
      globalEventStream.subscribe('motion.state.query', handleMotionStateQuery),
    ];

    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());
      stopHold();
    };
  }, [handleStart, handleProgress, handleReady, handleNavigationUpdate, handleClose, handleSettingsChanged, handleChartActivated, handleMotionStateQuery, stopHold]);

  // ===========================================================================
  // Update foreignObject pointer-events based on visibility
  // ===========================================================================

  useEffect(() => {
    if (!portalTarget) return;

    const foreignObject = portalTarget.closest('foreignObject');
    if (foreignObject) {
      foreignObject.setAttribute('pointer-events', state.type !== 'hidden' ? 'auto' : 'none');
      portalTarget.style.pointerEvents = state.type !== 'hidden' ? 'auto' : 'none';
    }
  }, [state.type, portalTarget]);

  // ===========================================================================
  // Render
  // ===========================================================================

  if (state.type === 'hidden') {
    return null;
  }

  const renderContent = () => {
    if (state.type === 'loading') {
      return (
        <div className="planet-drag-overlay planet-drag-overlay--loading planet-drag-overlay--in-svg">
          <div className="planet-drag-overlay__content">
            <div className="planet-drag-overlay__spinner" />
            <span className="planet-drag-overlay__text">
              Loading {state.stepValue}{formatStepUnitShort(state.stepUnit)} buffer...
            </span>
            {state.progress > 0 && (
              <div className="planet-drag-overlay__progress">
                <div
                  className="planet-drag-overlay__progress-bar"
                  style={{ width: `${state.progress}%` }}
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (state.type === 'navigating') {
      // Calculate offset from original chart time (in seconds)
      const offsetSeconds = Math.round((state.currentTime.getTime() - state.originalChartTime.getTime()) / 1000);
      const hasChanged = offsetSeconds !== 0;

      // Calculate step in seconds for buffer position
      const stepInSeconds = state.stepValue * (state.stepUnit === 'second' ? 1 : state.stepUnit === 'minute' ? 60 : 3600);
      const indexOffset = Math.round(offsetSeconds / stepInSeconds);

      return (
        <div className="planet-drag-overlay planet-drag-overlay--navigating planet-drag-overlay--in-svg">
          <div className="planet-drag-overlay__nav-container">
            {/* Back button */}
            <NavButton
              direction="back"
              disabled={!state.canGoBack}
              waiting={waitingDirection === 'back'}
              isPartial={state.isPartial ?? false}
              onNavigate={() => startHold('back')}
              onDisabledClick={() => handleDisabledClick('back')}
              onStop={stopHold}
            />

            {/* Time display */}
            <div className="planet-drag-overlay__time-display">
              {/* Step info */}
              <span className="planet-drag-overlay__planet">
                Time Step: {state.stepValue}{formatStepUnitShort(state.stepUnit)}
              </span>

              {/* Offset indicator - show formatted time difference from original */}
              <span className="planet-drag-overlay__coordinates">
                {formatOffsetFromSeconds(offsetSeconds)}
              </span>

              {/* Buffer position indicator - use actual buffer indices for correct display */}
              <BufferIndicator
                currentIndex={state.currentIndex}
                centerIndex={state.centerIndex}
                bufferSize={TIME_BUFFER_SIZE}
                loadingProgress={state.loadingProgress ?? 100}
              />

              {/* Current time */}
              <span className="planet-drag-overlay__time">
                {formatTime(state.currentTime, state.timezoneCorrection)}
              </span>
              <span className="planet-drag-overlay__date">
                {formatDate(state.currentTime, state.timezoneCorrection)}
              </span>
            </div>

            {/* Forward button */}
            <NavButton
              direction="forward"
              disabled={!state.canGoForward}
              waiting={waitingDirection === 'forward'}
              isPartial={state.isPartial ?? false}
              onNavigate={() => startHold('forward')}
              onDisabledClick={() => handleDisabledClick('forward')}
              onStop={stopHold}
            />
          </div>

          {/* Action buttons */}
          <ActionButtons
            hasChanged={hasChanged}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
          />

          {/* Hint */}
          <NavigationHint />
        </div>
      );
    }

    return null;
  };

  const content = renderContent();
  if (!content) return null;

  if (portalTarget) {
    return createPortal(content, portalTarget);
  }

  return content;
};

// =============================================================================
// Helpers
// =============================================================================

function formatStepUnitShort(unit: TimeStepUnit): string {
  switch (unit) {
    case 'second': return 's';
    case 'minute': return 'm';
    case 'hour': return 'h';
    default: return '';
  }
}

export default TimeMotionOverlay;
